import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { UsageExplorerManagerRowDto } from "../../../types/analytics/AnalyticsPageDataDto";
import { getManagerDistributionData } from "../../../helpers/usage/usageDistributionHelpers";

interface UsageManagerDistributionProps {
  managerRows: UsageExplorerManagerRowDto[];
  isMobile?: boolean;
  onSelectManager: (managerEmployeeId: number) => void;
}

function UsageManagerDistribution({
  managerRows,
  isMobile = false,
  onSelectManager,
}: UsageManagerDistributionProps) {
  const { t } = useTranslation();
  const distribution = getManagerDistributionData(managerRows);
  const chartRows = [...distribution.rows].reverse();

  if (isMobile) {
    return (
      <p className="rounded-lg border border-gray-200 px-4 py-10 text-center text-sm text-gray-600">
        {t(
          "Analytics.Widgets.Usage.Explorer.Distribution.MobileUnavailable",
          "The distribution chart is best viewed on a larger screen. Switch to table view to explore managers on mobile.",
        )}
      </p>
    );
  }

  if (distribution.totalCount === 0) {
    return (
      <p className="rounded-lg border border-gray-200 px-4 py-10 text-center text-sm text-gray-600">
        {t(
          "Analytics.Widgets.Usage.Explorer.Distribution.Empty",
          "There are no manager completion rates to plot for this view.",
        )}
      </p>
    );
  }

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-4"
      role="region"
      aria-label={t(
        "Analytics.Widgets.Usage.Explorer.Distribution.ChartLabel",
        "Manager completion distribution",
      )}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <ul
          className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-600"
          aria-label={t(
            "Analytics.Widgets.Usage.Explorer.Distribution.BandsLabel",
            "Completion bands",
          )}
        >
          <DistributionBand colourClass="bg-gray-300">
            {t(
              "Analytics.Widgets.Usage.Explorer.Distribution.BandLow",
              "Below 25%",
            )}
          </DistributionBand>
          <DistributionBand colourClass="bg-amber-300">
            {t(
              "Analytics.Widgets.Usage.Explorer.Distribution.BandMiddle",
              "25% to 74%",
            )}
          </DistributionBand>
          <DistributionBand colourClass="border border-primary bg-[color:var(--color-primary-pale)]">
            {t(
              "Analytics.Widgets.Usage.Explorer.Distribution.BandHigh",
              "75% and above",
            )}
          </DistributionBand>
        </ul>

        <p className="text-xs text-gray-500">
          {distribution.rows.length === distribution.totalCount &&
          distribution.totalCount === 1
            ? t(
                "Analytics.Widgets.Usage.Explorer.Distribution.ShowingOne",
                "Showing 1 manager",
              )
            : distribution.rows.length === distribution.totalCount
            ? t(
                "Analytics.Widgets.Usage.Explorer.Distribution.ShowingAll",
                "Showing all {{count}} managers",
                { count: distribution.totalCount },
              )
            : t(
                "Analytics.Widgets.Usage.Explorer.Distribution.ShowingCapped",
                "Showing {{shown}} of {{total}} managers, ranked worst-first",
                {
                  shown: distribution.rows.length,
                  total: distribution.totalCount,
                },
              )}
        </p>
      </div>

      <div className="h-[380px] min-h-[320px] w-full" aria-hidden>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 16, right: 20, bottom: 30, left: 44 }}
          >
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <ReferenceArea y1={0} y2={25} fill="#e5e7eb" fillOpacity={0.45} />
            <ReferenceArea y1={25} y2={75} fill="#fde68a" fillOpacity={0.25} />
            <ReferenceArea
              y1={75}
              y2={100}
              fill="var(--color-primary-pale)"
              fillOpacity={0.28}
            />
            <XAxis
              type="number"
              dataKey="teamSize"
              allowDecimals={false}
              domain={[0, "dataMax + 1"]}
              tick={{ fill: "#4b5563", fontSize: 12 }}
              label={{
                value: t(
                  "Analytics.Widgets.Usage.Explorer.Distribution.TeamSizeAxis",
                  "Team size",
                ),
                position: "insideBottom",
                offset: -18,
                fill: "#4b5563",
              }}
            />
            <YAxis
              type="number"
              dataKey="completionPercent"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: "#4b5563", fontSize: 12 }}
              width={60}
              label={{
                value: t(
                  "Analytics.Widgets.Usage.Explorer.Distribution.CompletionAxis",
                  "Completion",
                ),
                angle: -90,
                position: "insideLeft",
                fill: "#4b5563",
              }}
            />
            <Tooltip
              cursor={{ stroke: "#9ca3af", strokeDasharray: "3 3" }}
              content={<ManagerDistributionTooltip />}
            />
            <Scatter
              data={chartRows}
              isAnimationActive={false}
              shape={(props: any) => (
                <ManagerScatterPoint
                  {...props}
                  onSelect={onSelectManager}
                />
              )}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <details className="mt-3 border-t border-gray-200 pt-3">
        <summary className="w-fit cursor-pointer text-sm font-semibold text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]">
          {t(
            "Analytics.Widgets.Usage.Explorer.Distribution.ManagerList",
            "Managers in this chart",
          )}
        </summary>
        <ul className="mt-2 grid max-h-48 gap-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
          {distribution.rows.map((manager) => (
            <li key={manager.managerEmployeeId}>
              <button
                type="button"
                className="w-full rounded px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)]"
                onClick={() => onSelectManager(manager.managerEmployeeId)}
              >
                {manager.managerName ||
                  t(
                    "Analytics.Widgets.Usage.Explorer.UnknownManager",
                    "Unknown manager",
                  )}
                <span className="ml-2 text-xs text-gray-500">
                  {manager.completionPercent}%
                </span>
              </button>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}

function DistributionBand({
  colourClass,
  children,
}: {
  colourClass: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={`h-2.5 w-2.5 rounded-sm ${colourClass}`}
        aria-hidden
      />
      {children}
    </li>
  );
}

function ManagerScatterPoint({
  cx,
  cy,
  payload,
  onSelect,
}: {
  cx?: number;
  cy?: number;
  payload?: UsageExplorerManagerRowDto;
  onSelect: (managerEmployeeId: number) => void;
}) {
  if (cx === undefined || cy === undefined || !payload) return <g />;

  const openManager = () => onSelect(payload.managerEmployeeId);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill="#374151"
      stroke="#ffffff"
      strokeWidth={1}
      aria-hidden
      onClick={openManager}
      className="cursor-pointer"
    />
  );
}

function ManagerDistributionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: UsageExplorerManagerRowDto }>;
}) {
  const { t } = useTranslation();
  const manager = payload?.[0]?.payload;
  if (!active || !manager) return null;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow">
      <p className="font-semibold text-gray-800">
        {manager.managerName ||
          t(
            "Analytics.Widgets.Usage.Explorer.UnknownManager",
            "Unknown manager",
          )}
      </p>
      <p className="mt-1 text-gray-600">
        {manager.teamSize === 1
          ? t(
              "Analytics.Widgets.Usage.Explorer.Distribution.TooltipSingle",
              "1 person · {{completionPercent}}% complete",
              { completionPercent: manager.completionPercent },
            )
          : t(
              "Analytics.Widgets.Usage.Explorer.Distribution.TooltipMultiple",
              "{{teamSize}} people · {{completionPercent}}% complete",
              {
                teamSize: manager.teamSize,
                completionPercent: manager.completionPercent,
              },
            )}
      </p>
    </div>
  );
}

export default UsageManagerDistribution;
